// src/components/FaunaIcons.jsx
import React from 'react';

/**
 * FaunaIcons renders all the little fauna bubbles for a zone.
 *
 * Props:
 * - fauna: array of fauna objects, each with:
 *     lightTypeDepth, darkTypeDepth, lightTypeUrl, darkTypeUrl, lightType, darkType, javaVersion, blogPost, name
 * - depthRange: [minDepth, maxDepth]
 * - theme: 'light' | 'dark'
 * - highlightVersion: string | null
 * - horizontalOffsets: array of percentages (e.g. ['12%', '78%', ...])
 * - onClick: fn(faunaItem)
 */
export default function FaunaIcons({
  fauna,
  depthRange,
  theme,
  highlightVersion,
  horizontalOffsets,
  onClick,
}) {
  const [minD, maxD] = depthRange;
  return (
    <>
      {fauna.map((item, idx) => {
        // pick the right depth value
        const raw = theme === 'light'
          ? parseFloat(item.lightTypeDepth)
          : parseFloat(item.darkTypeDepth);
        // normalize to 0–1, fallback to 0.5 if bad
        const frac = Number.isFinite(raw)
          ? (raw - minD) / (maxD - minD)
          : 0.5;
        const top = `${Math.min(Math.max(frac * 100, 0), 100)}%`;
        const left = horizontalOffsets[idx];

        // highlight logic
        const isHighlighted = highlightVersion && item.javaVersion === highlightVersion;
        const baseOpacity = highlightVersion ? (isHighlighted ? 1 : 0.4) : 1;
        const ringClass = isHighlighted ? 'rounded-full ring-4 ring-yellow-300 scale-110' : '';

        // choose image vs. label
        const iconUrl = theme === 'light' ? item.lightTypeUrl : item.darkTypeUrl;
        const label   = theme === 'light' ? item.lightType    : item.darkType;

        return (
          <div
            key={item.name}
            onClick={() => onClick(item)}
            className={`
              absolute flex items-center justify-center
              w-12 h-12 cursor-pointer
              transition-all duration-300 ease-linear
              opacity-${baseOpacity * 100}
              ${ringClass}
            `}
            style={{ top, left }}
          >
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={label}
                className="w-full h-full "
              />
            ) : (
              <span
                className={`
                  ${theme === 'light'
                    ? 'border-white text-white bg-black bg-opacity-30'
                    : 'border-gray-800 text-gray-200 bg-gray-900 bg-opacity-30'}
                `}
              >
                {label}
              </span>
            )}
          </div>
        );
      })}
    </>
  );
}
